import React from 'react';
import { Input } from '../ui/input';
import { Search, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from '../ui/dropdown-menu';

export default function DataToolbar({ search, onSearch, placeholder, filters = [], right }) {
  return (
    <div className="flex items-center gap-2 flex-wrap mb-5">
      <div className="relative flex-1 min-w-[220px] max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-[15px] w-[15px] text-slate-400" strokeWidth={2} />
        <Input
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder={placeholder || 'Search…'}
          className="pl-9 h-10 bg-white border-slate-200/80 text-[13px] placeholder:text-slate-400 rounded-lg focus-visible:ring-teal-500/40 focus-visible:border-teal-500/60"
        />
      </div>
      {filters.map((f) => (
        <DropdownMenu key={f.label}>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="h-10 border-slate-200/80 bg-white gap-1.5 text-[13px] font-medium rounded-lg text-slate-700 hover:bg-slate-50">
              <span className="text-slate-500">{f.label}</span>
              {f.value ? <span className="text-slate-900">: {f.value}</span> : null}
              <ChevronDown className="h-3.5 w-3.5 text-slate-400" strokeWidth={2} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-[160px]">
            <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{f.label}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {f.options.map((opt) => (
              <DropdownMenuItem key={opt} onClick={() => f.onChange(opt === 'All' ? '' : opt)} className="text-[13px]">{opt}</DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ))}
      <div className="ml-auto flex items-center gap-2">{right}</div>
    </div>
  );
}

export function StatusPill({ status }) {
  const map = {
    active: 'bg-emerald-50 text-emerald-700 ring-emerald-200/70',
    published: 'bg-emerald-50 text-emerald-700 ring-emerald-200/70',
    confirmed: 'bg-emerald-50 text-emerald-700 ring-emerald-200/70',
    completed: 'bg-sky-50 text-sky-700 ring-sky-200/70',
    paid: 'bg-emerald-50 text-emerald-700 ring-emerald-200/70',
    pending: 'bg-amber-50 text-amber-700 ring-amber-200/70',
    draft: 'bg-slate-100 text-slate-700 ring-slate-200/70',
    unpublished: 'bg-slate-100 text-slate-700 ring-slate-200/70',
    suspended: 'bg-rose-50 text-rose-700 ring-rose-200/70',
    cancelled: 'bg-rose-50 text-rose-700 ring-rose-200/70',
    rejected: 'bg-rose-50 text-rose-700 ring-rose-200/70',
    refunded: 'bg-slate-100 text-slate-700 ring-slate-200/70',
    open: 'bg-amber-50 text-amber-700 ring-amber-200/70',
    in_progress: 'bg-sky-50 text-sky-700 ring-sky-200/70',
    resolved: 'bg-emerald-50 text-emerald-700 ring-emerald-200/70',
    booked: 'bg-emerald-50 text-emerald-700 ring-emerald-200/70',
    unavailable: 'bg-rose-50 text-rose-700 ring-rose-200/70',
  };
  const cls = map[status] || 'bg-slate-100 text-slate-700 ring-slate-200/70';
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-[3px] rounded-md text-[10.5px] font-medium ring-1 ring-inset ${cls}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-80" />
      <span className="tracking-tight">{(status || '').replace('_', ' ')}</span>
    </span>
  );
}

export function Card({ children, className = '' }) {
  return <div className={`rounded-2xl bg-white border border-slate-200/80 ${className}`}>{children}</div>;
}
